import React, { useState, useRef, useEffect } from "react";
import "./styles/styles.css";
import "../../styles/global.css";
import AccreditationButton from "../AccreditationButton/AccreditationButton";
import { icons } from "../../constants";

const CustomImageCropper = ({
  localSrc,
  cropWidth = 300,
  cropHeight = 300,
  onCropComplete,
  onCloseClick,
}) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const prevSrcRef = useRef(null);

  const [baseImgDimensions, setBaseImgDimensions] = useState({
    width: 0,
    height: 0,
  });
  // Where the displayed image starts inside .image-cropper-image-wrap. The
  // wrap centers the image (it's usually wider/taller than the image), but
  // the crop box is absolutely positioned against the wrap — without this
  // offset the box sat shifted left/up from the image, couldn't reach its
  // right/bottom edges, and the saved crop didn't match what was shown.
  const [imgOffset, setImgOffset] = useState({ left: 0, top: 0 });

  const [cropBox, setCropBox] = useState({
    x: 0,
    y: 0,
    size: cropWidth,
  });

  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [initialCropBox, setInitialCropBox] = useState(null);

  const minSize = 100;
  // No fixed max here on purpose — the displayed image can render much
  // larger than any reasonable flat cap (up to viewport height minus 220px,
  // see .image-cropper-image-wrap), so a hardcoded ceiling like the 600px
  // this used to be stops the box well short of the actual image edges on
  // any reasonably large photo. The real ceiling is the image's own bounds,
  // computed per-drag below as maxSizeForBounds.

  const resetCropBox = () => {
    if (!imgRef.current) return;

    const baseWidth = imgRef.current.clientWidth;
    const baseHeight = imgRef.current.clientHeight;
    // Never start bigger than the image itself (small photos).
    const startSize = Math.min(cropWidth, cropHeight, baseWidth, baseHeight);

    setBaseImgDimensions({ width: baseWidth, height: baseHeight });
    setImgOffset({
      left: imgRef.current.offsetLeft,
      top: imgRef.current.offsetTop,
    });
    setCropBox({
      x: (baseWidth - startSize) / 2,
      y: (baseHeight - startSize) / 2,
      size: startSize,
    });

    setDragging(false);
    setResizing(false);
    setStartPos({ x: 0, y: 0 });
    setInitialCropBox(null);
  };

  useEffect(() => {
    if (prevSrcRef.current !== localSrc) {
      prevSrcRef.current = localSrc;
    }
    resetCropBox();
  }, [localSrc]);

  // The image is sized to the viewport, so its displayed size and position
  // change on resize — re-measure (resets the box to the new bounds).
  useEffect(() => {
    window.addEventListener("resize", resetCropBox);
    return () => window.removeEventListener("resize", resetCropBox);
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setStartPos({ x: e.clientX, y: e.clientY });
    setInitialCropBox({ ...cropBox });
  };

  const handleMouseMove = (e) => {
    if (!dragging && !resizing) return;

    const deltaX = e.clientX - startPos.x;
    const deltaY = e.clientY - startPos.y;

    if (dragging) {
      let newX = initialCropBox.x + deltaX;
      let newY = initialCropBox.y + deltaY;

      newX = Math.max(
        0,
        Math.min(baseImgDimensions.width - cropBox.size, newX),
      );
      newY = Math.max(
        0,
        Math.min(baseImgDimensions.height - cropBox.size, newY),
      );

      setCropBox((prev) => ({ ...prev, x: newX, y: newY }));
    }

    if (resizing) {
      // Grow up to the largest square the image allows (its shorter side).
      // When the box hits the right/bottom edge it slides back up/left
      // instead of stopping, so one drag of the handle can reach
      // edge-to-edge from wherever the box started.
      const maxSize = Math.min(
        baseImgDimensions.width,
        baseImgDimensions.height,
      );
      const newSize = Math.max(
        Math.min(minSize, maxSize),
        Math.min(maxSize, initialCropBox.size + deltaX),
      );

      setCropBox({
        size: newSize,
        x: Math.max(
          0,
          Math.min(initialCropBox.x, baseImgDimensions.width - newSize),
        ),
        y: Math.max(
          0,
          Math.min(initialCropBox.y, baseImgDimensions.height - newSize),
        ),
      });
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
    setResizing(false);
  };

  useEffect(() => {
    if (dragging || resizing) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, resizing]);

  const handleCrop = () => {
    if (!imgRef.current) return;
    const image = imgRef.current;

    const canvas = document.createElement("canvas");
    canvas.width = 800;
    canvas.height = 800;
    const ctx = canvas.getContext("2d");

    const scaleX = image.naturalWidth / baseImgDimensions.width;
    const scaleY = image.naturalHeight / baseImgDimensions.height;

    ctx.drawImage(
      image,
      cropBox.x * scaleX,
      cropBox.y * scaleY,
      cropBox.size * scaleX,
      cropBox.size * scaleY,
      0,
      0,
      800,
      800,
    );

    const croppedDataUrl = canvas.toDataURL("image/jpeg", 0.92);

    if (onCropComplete) {
      onCropComplete(croppedDataUrl);
      onCloseClick();
    }
  };

  return (
    <div className="flex-direction-row justify-center items-center image-cropper">
      <div className="flex-direction-column justify-between items-center image-cropper-content">
        <div className="flex-direction-row justify-between width-100">
          <img
            className="pointer"
            src={icons.close_gray}
            onClick={onCloseClick}
            alt="Close"
          />
        </div>

        {localSrc ? (
          <div
            ref={containerRef}
            className="image-cropper-image-wrap"
          >
            <img
              ref={imgRef}
              src={localSrc}
              alt="crop"
              onLoad={resetCropBox}
            />
            <div
              onMouseDown={handleMouseDown}
              style={{
                position: "absolute",
                left: imgOffset.left + cropBox.x,
                top: imgOffset.top + cropBox.y,
                width: cropBox.size,
                height: cropBox.size,
                border: "2px dashed red",
                cursor: "move",
                backgroundColor: "rgba(255,255,255,0.2)",
              }}
            >
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setResizing(true);
                  setStartPos({ x: e.clientX, y: e.clientY });
                  setInitialCropBox({ ...cropBox });
                }}
                style={{
                  position: "absolute",
                  width: "20px",
                  height: "20px",
                  right: "-10px",
                  bottom: "-10px",
                  background: "red",
                  cursor: "nwse-resize",
                }}
              />
            </div>
          </div>
        ) : (
          <p>Slika ne postoji.</p>
        )}

        <AccreditationButton
          onClick={handleCrop}
          title="Crop"
          type="save"
          className="crop-btn"
        />
      </div>
    </div>
  );
};

export default CustomImageCropper;
